import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BLOG_POSTS } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

export function BlogIndex() {
    useReveal();
    return (
        <div className="pt-24" data-testid="blog-index">
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="ssh-reveal">
                    <SectionHeading
                        eyebrow="Insights"
                        title="Slow reads on healing,"
                        titleItalic="softness, and self-return."
                        sub="A growing collection of reflections on the inner work — gentle, grounded, and free to read."
                    />
                </div>
            </section>
            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {BLOG_POSTS.map((p, i) => (
                            <Link
                                to={`/blog/${p.slug}`}
                                key={p.slug}
                                className="group ssh-reveal"
                                style={{ transitionDelay: `${0.06 * i}s` }}
                                data-testid={`blog-card-${p.slug}`}
                            >
                                <p className="ssh-eyebrow">{p.category}</p>
                                <h3 className="mt-4 font-serif text-3xl text-[#2C2B29] leading-tight group-hover:text-[#A68A6B] transition-colors">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-base text-[#4A4947] leading-relaxed">
                                    {p.excerpt}
                                </p>
                                <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-[#75736F]">
                                    <span>{p.date}</span>
                                    <span>·</span>
                                    <span>{p.readTime}</span>
                                </div>
                                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] group-hover:gap-3 transition-all">
                                    Read more <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export function BlogPost() {
    useReveal();
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    // SEO: update document title + meta description while on this post
    useEffect(() => {
        if (!post) return;
        const prevTitle = document.title;
        const meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta ? meta.getAttribute("content") : null;
        document.title = `${post.title} · Soul Sync Healing`;
        if (meta) {
            meta.setAttribute(
                "content",
                post.metaDescription || post.excerpt || prevDesc || "",
            );
        }
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc !== null) {
                meta.setAttribute("content", prevDesc);
            }
        };
    }, [post]);

    if (!post) return <Navigate to="/blog" replace />;

    const renderBlock = (block, i) => {
        if (typeof block === "string") {
            return (
                <p
                    key={i}
                    className="text-lg leading-[1.85] text-[#2C2B29] font-light"
                >
                    {block}
                </p>
            );
        }
        if (block.type === "h2") {
            return (
                <h2
                    key={i}
                    className="font-serif text-3xl sm:text-4xl text-[#2C2B29] leading-tight mt-12 mb-2"
                >
                    {block.text}
                </h2>
            );
        }
        if (block.type === "p") {
            if (block.html) {
                return (
                    <p
                        key={i}
                        className="text-lg leading-[1.85] text-[#2C2B29] font-light"
                        dangerouslySetInnerHTML={{ __html: block.html }}
                    />
                );
            }
            return (
                <p
                    key={i}
                    className="text-lg leading-[1.85] text-[#2C2B29] font-light"
                >
                    {block.text}
                </p>
            );
        }
        return null;
    };

    return (
        <div className="pt-24" data-testid={`blog-post-${post.slug}`}>
            <article className="max-w-3xl mx-auto px-6 lg:px-0 py-16 lg:py-24">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                    data-testid="blog-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                    All Insights
                </Link>
                <div className="mt-10 ssh-reveal">
                    <p className="ssh-eyebrow">{post.category}</p>
                    <h1 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2C2B29]">
                        {post.title}
                    </h1>
                    <div className="mt-5 flex items-center gap-4 text-xs uppercase tracking-[0.18em] text-[#75736F]">
                        <span>{post.date}</span>
                        <span>·</span>
                        <span>{post.readTime}</span>
                    </div>
                </div>
                {post.image && (
                    <div className="mt-12 aspect-[16/9] overflow-hidden ssh-reveal">
                        <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>
                )}
                <div className="mt-12 space-y-6 ssh-reveal">
                    {post.body.map(renderBlock)}
                </div>
                <div className="mt-16 pt-10 border-t border-[#EFEBE4] text-center ssh-reveal">
                    <p className="ssh-eyebrow">Continue reading</p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {BLOG_POSTS.filter((p) => p.slug !== post.slug)
                            .slice(0, 2)
                            .map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    className="group text-left p-6 border border-[#EFEBE4] hover:border-[#A68A6B] transition-colors"
                                >
                                    <p className="ssh-eyebrow">{p.category}</p>
                                    <p className="mt-2 font-serif text-xl text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors">
                                        {p.title}
                                    </p>
                                </Link>
                            ))}
                    </div>
                </div>
            </article>
        </div>
    );
}
